import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Clock, CheckCircle, Wrench, Snowflake, Flame, Shield, Users, ArrowLeft, Star, HardHat } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import SEOHead, { SITE_URL } from '../components/SEOHead';

const serviceData = {
  'heating-services': {
    icon: <Flame className="w-12 h-12" />,
    title: 'Heating Services',
    description: 'Expert furnace installation, repair, and maintenance for homes and businesses in Tacoma, Puyallup, and Pierce County, WA.',
    image: 'https://images.unsplash.com/photo-1758545814875-c2f0a0c89943?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTV8MHwxfHNlYXJjaHwyfHxIVkFDJTIwZXF1aXBtZW50fGVufDB8fHx8MTc3MTI2NDg2MHww&ixlib=rb-4.1.0&q=85',
    seoTitle: 'Heating Services | Furnace Installation & Repair | Tacoma, Puyallup, Pierce County WA',
    seoDescription: 'Professional heating services in Pierce County, WA. Furnace installation, heat pump service, boiler repair, and heating maintenance in Tacoma, Puyallup, Lakewood. Licensed HVAC contractor. Call (253) 501-7893.',
    seoKeywords: 'heating services Pierce County, furnace installation Tacoma, furnace repair Puyallup, heat pump installation WA, heating contractor Lakewood, boiler repair Pierce County, heating system maintenance, furnace tune-up Tacoma',
    details: [
      'Furnace installation and replacement',
      'Heat pump installation and service',
      'Boiler repair and maintenance',
      'Ductwork inspection and sealing',
      'Thermostat installation and programming',
      'Annual heating system tune-ups'
    ],
    longDescription: 'When temperatures drop in Pierce County, you need a heating system you can rely on. As a licensed contractor in Washington, our team of certified technicians specializes in all types of heating systems, from traditional furnaces to modern heat pumps. We provide thorough inspections, efficient repairs, and expert installations for homeowners and businesses across Tacoma, Puyallup, Lakewood, and surrounding areas to ensure your property stays warm and comfortable throughout the coldest months.'
  },
  'air-conditioning': {
    icon: <Snowflake className="w-12 h-12" />,
    title: 'Air Conditioning',
    description: 'Professional AC installation, repair, and tune-ups serving Puyallup, Tacoma, and all of Pierce County, WA.',
    image: 'https://images.pexels.com/photos/24828656/pexels-photo-24828656.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    seoTitle: 'Air Conditioning | AC Installation & Repair | Tacoma, Puyallup, Pierce County WA',
    seoDescription: 'Expert air conditioning services in Pierce County, WA. AC installation, AC repair, ductless mini splits, and cooling maintenance in Tacoma, Puyallup, Lakewood. Licensed HVAC contractor. Call (253) 501-7893.',
    seoKeywords: 'air conditioning Pierce County, AC installation Tacoma, AC repair Puyallup, ductless mini split Lakewood, air conditioning contractor WA, central AC installation, AC tune-up Pierce County, cooling system repair',
    details: [
      'Central air conditioning installation',
      'AC repair and troubleshooting',
      'Ductless mini-split systems',
      'Air conditioning tune-ups',
      'Refrigerant recharge and leak repair',
      'Indoor air quality assessments'
    ],
    longDescription: 'Stay cool and comfortable during the hottest days in Pierce County with our professional air conditioning services. From installing energy-efficient central air systems in Puyallup to repairing and maintaining units across Tacoma and Lakewood, our licensed technicians ensure your AC runs at peak performance. We also offer ductless mini-split solutions for spaces without existing ductwork — a popular choice for homes and businesses throughout the greater Tacoma area.'
  },
  'repair-maintenance': {
    icon: <Wrench className="w-12 h-12" />,
    title: 'Repair & Maintenance',
    description: 'Fast, reliable HVAC repairs and preventive maintenance from a licensed contractor in Washington state.',
    image: 'https://images.pexels.com/photos/32497161/pexels-photo-32497161.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    seoTitle: 'HVAC Repair & Maintenance | Mechanical Repairs | Tacoma, Puyallup, Pierce County WA',
    seoDescription: 'Fast HVAC repair and preventive maintenance in Pierce County, WA. Mechanical repairs, system diagnostics, filter replacement, and performance optimization in Tacoma, Puyallup. Call (253) 501-7893.',
    seoKeywords: 'HVAC repair Pierce County, HVAC maintenance Tacoma, mechanical repairs Puyallup, furnace repair near me, AC repair near me, HVAC diagnostics, preventive maintenance, HVAC tune-up Lakewood',
    details: [
      'Comprehensive system diagnostics',
      'Preventive maintenance plans',
      'Filter replacement and cleaning',
      'Electrical component repair',
      'Blower motor and fan repair',
      'System performance optimization'
    ],
    longDescription: 'Regular maintenance is key to keeping your HVAC system running efficiently and avoiding costly breakdowns. Our preventive maintenance plans include thorough inspections, cleaning, and tune-ups that extend the life of your equipment, lower energy bills, and ensure your property stays comfortable year-round. Serving Tacoma, Puyallup, Lakewood, and communities across Pierce County, our experienced Washington-licensed technicians diagnose and fix issues quickly when repairs are needed.'
  },
  'new-construction': {
    icon: <HardHat className="w-12 h-12" />,
    title: 'New Construction HVAC',
    description: 'Complete HVAC system design and installation for new residential and commercial construction projects in Pierce County, WA.',
    image: 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?crop=entropy&cs=srgb&fm=jpg&ixid=M3wxMjA3fDB8MHxzZWFyY2h8Mnx8bmV3JTIwY29uc3RydWN0aW9ufGVufDB8fDB8fHww&ixlib=rb-4.1.0&q=85',
    seoTitle: 'New Construction HVAC | Heating & Cooling for New Builds | Pierce County WA',
    seoDescription: 'New construction HVAC design and installation in Pierce County, WA. Complete heating and cooling systems for new residential and commercial builds in Tacoma, Puyallup, Bonney Lake. Licensed contractor. Call (253) 501-7893.',
    seoKeywords: 'new construction HVAC Pierce County, HVAC new build Tacoma, new home heating Puyallup, new construction heating and cooling, HVAC design new construction WA, ductwork installation new build, commercial new construction HVAC, residential new construction heating',
    details: [
      'Custom HVAC system design for new builds',
      'Load calculation and equipment sizing',
      'Complete ductwork design and installation',
      'Energy-efficient system selection',
      'Heat pump and furnace installation for new homes',
      'Commercial new construction HVAC solutions',
      'Smart thermostat and zoning system integration',
      'Code compliance and permit coordination',
      'Builder and general contractor coordination',
      'Post-installation testing and commissioning'
    ],
    longDescription: 'Building a new home or commercial property in Pierce County? Getting the HVAC system right from the start is one of the most important decisions for long-term comfort and energy efficiency. As a licensed contractor in Washington, Taking Over Contracting Inc provides complete new construction HVAC services — from initial system design and load calculations to full installation, ductwork, and commissioning. We work directly with builders, general contractors, and property owners across Tacoma, Puyallup, Bonney Lake, Lakewood, and all of Pierce County to deliver heating and cooling systems that are properly sized, energy-efficient, and built to last. Whether it\'s a single-family home, multi-unit residential, or commercial new build, our experienced technicians handle every step of the HVAC installation process.'
  },
  'emergency-service': {
    icon: <Clock className="w-12 h-12" />,
    title: 'Emergency Service',
    description: '24/7 emergency HVAC services in Pierce County, WA. We\'re always here when you need us most.',
    image: 'https://images.unsplash.com/photo-1662556153586-223039dc8152?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHwxfHxIVkFDJTIwdGVjaG5pY2lhbnxlbnwwfHx8fDE3NzEyNjQ4NTZ8MA&ixlib=rb-4.1.0&q=85',
    seoTitle: '24/7 Emergency HVAC Service | Tacoma, Puyallup, Pierce County WA',
    seoDescription: '24/7 emergency HVAC service in Pierce County, WA. Same-day heating and AC repair, emergency furnace service, and urgent HVAC repairs in Tacoma, Puyallup, Lakewood. Call (253) 501-7893 now.',
    seoKeywords: 'emergency HVAC Pierce County, 24/7 HVAC repair Tacoma, emergency furnace repair Puyallup, emergency AC repair, same-day HVAC service, after hours HVAC Lakewood, emergency heating repair, urgent HVAC service WA',
    details: [
      '24/7 emergency response',
      'Same-day service availability',
      'No extra charge for after-hours diagnostics',
      'Rapid heating system restoration',
      'Emergency AC repair',
      'Gas leak detection and response'
    ],
    longDescription: 'HVAC emergencies don\'t wait for business hours, and neither do we. Our emergency service team is available around the clock to handle urgent heating and cooling issues across Tacoma, Puyallup, Lakewood, Spanaway, and all of Pierce County, WA. Whether your furnace stops working in the middle of a Washington winter or your AC fails during a summer heat wave, our licensed contractors respond quickly to restore your comfort and safety.'
  },
  'commercial-hvac': {
    icon: <Users className="w-12 h-12" />,
    title: 'Commercial HVAC',
    description: 'Comprehensive commercial heating and cooling solutions for businesses in Tacoma, Puyallup, and Pierce County, WA.',
    image: 'https://images.unsplash.com/photo-1751486289950-5c4898a4c773?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTV8MHwxfHNlYXJjaHw0fHxIVkFDJTIwZXF1aXBtZW50fGVufDB8fHx8MTc3MTI2NDg2MHww&ixlib=rb-4.1.0&q=85',
    seoTitle: 'Commercial HVAC Services | Tacoma, Puyallup, Pierce County WA',
    seoDescription: 'Commercial HVAC services in Pierce County, WA. System design, rooftop units, building automation, and commercial maintenance in Tacoma, Puyallup, Lakewood. Licensed contractor. Call (253) 501-7893.',
    seoKeywords: 'commercial HVAC Pierce County, commercial heating Tacoma, commercial AC Puyallup, rooftop HVAC unit installation, commercial HVAC maintenance, building automation systems, commercial HVAC contractor WA, business HVAC service',
    details: [
      'Commercial HVAC system design',
      'Rooftop unit installation and repair',
      'Building automation systems',
      'Commercial preventive maintenance contracts',
      'Energy efficiency audits',
      'Ventilation and air quality solutions'
    ],
    longDescription: 'Your business deserves a reliable HVAC system that keeps employees comfortable and operations running smoothly. As a licensed general contractor in Pierce County, WA, we provide comprehensive commercial HVAC services, from system design and installation to ongoing maintenance contracts. Our team has experience working with office buildings, retail spaces, warehouses, and other commercial properties of all sizes across Tacoma, Puyallup, Lakewood, and surrounding communities.'
  },
  'residential-hvac': {
    icon: <Shield className="w-12 h-12" />,
    title: 'Residential HVAC',
    description: 'Complete residential HVAC and remodeling services in Tacoma and Puyallup, tailored to your home comfort needs.',
    image: 'https://images.pexels.com/photos/7347538/pexels-photo-7347538.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    seoTitle: 'Residential HVAC Services | Home Heating & Cooling | Pierce County WA',
    seoDescription: 'Residential HVAC services in Pierce County, WA. Home heating and cooling installation, smart thermostat integration, duct cleaning, and energy-efficient upgrades in Tacoma, Puyallup. Call (253) 501-7893.',
    seoKeywords: 'residential HVAC Pierce County, home heating Tacoma, home AC Puyallup, residential heating contractor, home cooling system, smart thermostat installation, duct cleaning Pierce County, energy-efficient HVAC upgrade',
    details: [
      'Whole-home HVAC system installation',
      'Zoning systems for custom comfort',
      'Smart thermostat integration',
      'Duct cleaning and repair',
      'Indoor air quality solutions',
      'Energy-efficient system upgrades'
    ],
    longDescription: 'Your home should be your sanctuary, and that starts with the right HVAC system. As a licensed residential contractor in Washington, we offer complete home services designed to maximize comfort while minimizing energy costs. From whole-home installations to smart thermostat integration and indoor air quality improvements, we tailor our solutions to fit homes across Puyallup, Tacoma, Lakewood, Bonney Lake, and the greater Pierce County area.'
  }
};

const ServiceDetail = () => {
  const { slug } = useParams();
  const service = serviceData[slug];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!service) {
    return (
      <div className="min-h-screen bg-white">
        <header className="bg-black text-white py-4 px-6 fixed w-full top-0 z-50 shadow-lg">
          <div className="max-w-7xl mx-auto flex justify-between items-center">
            <Link to="/" className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-red-600 rounded-lg flex items-center justify-center">
                <Flame className="w-6 h-6 text-white" />
              </div>
              <h1 className="text-2xl font-bold">Taking Over Contracting Inc</h1>
            </Link>
          </div>
        </header>
        <div className="pt-24 pb-20 px-6 text-center">
          <h2 className="text-4xl font-bold text-black mb-4">Service Not Found</h2>
          <p className="text-gray-600 mb-8">The service you're looking for doesn't exist.</p>
          <Button asChild className="bg-red-600 hover:bg-red-700 text-white">
            <Link to="/">Back to Home</Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title={service.seoTitle || service.title}
        description={service.seoDescription || service.description}
        keywords={service.seoKeywords || ''}
        canonicalPath={`/services/${slug}`}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Service",
          "name": service.title,
          "description": service.longDescription,
          "provider": {
            "@type": "HVACBusiness",
            "name": "Taking Over Contracting Inc",
            "telephone": "(253) 501-7893",
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "1723 13th Ave Ct NW",
              "addressLocality": "Puyallup",
              "addressRegion": "WA",
              "postalCode": "98371",
              "addressCountry": "US"
            }
          },
          "areaServed": {
            "@type": "AdministrativeArea",
            "name": "Pierce County, Washington"
          },
          "serviceType": service.title
        }}
      />
      <header className="bg-black text-white py-4 px-6 fixed w-full top-0 z-50 shadow-lg">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <Link to="/" className="flex items-center space-x-3 hover:opacity-90 transition-opacity">
            <div className="w-10 h-10 bg-red-600 rounded-lg flex items-center justify-center">
              <Flame className="w-6 h-6 text-white" />
            </div>
            <h1 className="text-2xl font-bold">Taking Over Contracting Inc</h1>
          </Link>
          <div className="hidden md:flex items-center space-x-6">
            <Link to="/tips" className="hover:text-red-500 transition-colors font-semibold">HVAC Tips</Link>
            <Link to="/reviews" className="hover:text-red-500 transition-colors font-semibold flex items-center space-x-1">
              <Star className="w-4 h-4" />
              <span>Leave a Review</span>
            </Link>
            <a href="tel:2535017893" className="flex items-center space-x-2 hover:text-red-500 transition-colors">
              <Phone className="w-5 h-5" />
              <span className="font-semibold">(253) 501-7893</span>
            </a>
            <Button asChild className="bg-red-600 hover:bg-red-700 text-white">
              <Link to="/#contact">Get a Quote</Link>
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative pt-24">
        <div className="relative h-[400px] overflow-hidden">
          <img
            src={service.image}
            alt={service.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black bg-opacity-60 flex items-center">
            <div className="max-w-7xl mx-auto px-6 w-full">
              <div className="text-red-600 bg-white p-4 rounded-full inline-flex mb-6">
                {service.icon}
              </div>
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">{service.title}</h2>
              <p className="text-xl text-gray-200 max-w-2xl">{service.description}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Back Link */}
      <div className="max-w-7xl mx-auto px-6 py-6">
        <Button asChild variant="ghost" className="text-gray-600 hover:text-red-600 -ml-4">
          <Link to="/">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Link>
        </Button>
      </div>

      {/* Service Details */}
      <section className="pb-20 px-6">
        <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-12">
          <div>
            <h3 className="text-3xl font-bold text-black mb-6">About This Service</h3>
            <p className="text-lg text-gray-600 leading-relaxed mb-8">{service.longDescription}</p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                onClick={() => window.location.href = 'tel:2535017893'}
                className="bg-red-600 hover:bg-red-700 text-white text-lg px-8 py-6 rounded-lg shadow-lg hover:shadow-xl transition-all"
              >
                <Phone className="w-5 h-5 mr-2" />
                Call Now: (253) 501-7893
              </Button>
              <Button asChild className="bg-black hover:bg-gray-800 text-white text-lg px-8 py-6 rounded-lg shadow-lg hover:shadow-xl transition-all">
                <Link to="/#contact">Request Service</Link>
              </Button>
            </div>
          </div>

          <div>
            <Card className="shadow-xl border-2 border-gray-100">
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold text-black mb-6">What We Offer</h3>
                <ul className="space-y-4">
                  {service.details.map((detail, index) => (
                    <li key={index} className="flex items-start space-x-3">
                      <CheckCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700 text-lg">{detail}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-6 bg-black text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h3 className="text-3xl md:text-4xl font-bold mb-4">Ready to Get Started?</h3>
          <p className="text-xl text-gray-300 mb-8">
            Contact your trusted general contractor in Pierce County for a free estimate on {service.title.toLowerCase()}.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={() => window.location.href = 'tel:2535017893'}
              className="bg-red-600 hover:bg-red-700 text-white text-lg px-8 py-6 rounded-lg"
            >
              <Phone className="w-5 h-5 mr-2" />
              (253) 501-7893
            </Button>
            <Button asChild className="bg-white text-black hover:bg-gray-100 text-lg px-8 py-6 rounded-lg">
              <Link to="/#contact">Request a Quote</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black text-white py-12 px-6 border-t border-gray-800">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div>
              <Link to="/" className="flex items-center space-x-3 mb-4 hover:opacity-90 transition-opacity">
                <div className="w-10 h-10 bg-red-600 rounded-lg flex items-center justify-center">
                  <Flame className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-bold">Taking Over Contracting Inc</h3>
              </Link>
              <p className="text-gray-400">
                Your trusted licensed general contractor in Pierce County, WA. Professional HVAC, heating, cooling, and remodeling services in Tacoma, Puyallup, and surrounding areas.
              </p>
            </div>

            <div>
              <h4 className="text-lg font-bold mb-4">Quick Links</h4>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/" className="hover:text-red-500 transition-colors">Home</Link></li>
                <li><Link to="/#services" className="hover:text-red-500 transition-colors">Services</Link></li>
                <li><Link to="/tips" className="hover:text-red-500 transition-colors">HVAC Tips</Link></li>
                <li><Link to="/reviews" className="hover:text-red-500 transition-colors">Reviews</Link></li>
                <li><Link to="/#contact" className="hover:text-red-500 transition-colors">Contact</Link></li>
              </ul>
            </div>

            <div>
              <h4 className="text-lg font-bold mb-4">Contact Info</h4>
              <ul className="space-y-3 text-gray-400">
                <li className="flex items-center space-x-2">
                  <Phone className="w-4 h-4" />
                  <a href="tel:2535017893" className="hover:text-red-500 transition-colors">(253) 501-7893</a>
                </li>
                <li className="flex items-center space-x-2">
                  <Mail className="w-4 h-4" />
                  <a href="mailto:josh.takingovercontracting@gmail.com" className="hover:text-red-500 transition-colors text-sm">
                    josh.takingovercontracting@gmail.com
                  </a>
                </li>
                <li className="flex items-start space-x-2">
                  <MapPin className="w-4 h-4 mt-1" />
                  <span className="text-sm">1723 13th Ave Ct NW<br />Puyallup, WA 98371</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 pt-8 text-center text-gray-400">
            <p className="mb-2">UBI# 60589170 &nbsp;|&nbsp; License# TAKINOC755K2</p>
            <p>&copy; {new Date().getFullYear()} Taking Over Contracting Inc. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ServiceDetail;
